<script setup lang="ts">
import type { IconDefinition } from '@fortawesome/fontawesome-common-types'
import { computed, useAttrs, useSlots } from 'vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faSpinnerThird } from '@fortawesome/pro-duotone-svg-icons'
import { isSlotEmpty } from '@/helpers/components'

export interface ButtonBaseProps {
  shadow?: boolean
  rounded?: boolean
  size?: 'small' | 'large'
  icon?: IconDefinition
  iconPosition?: 'before' | 'after'
  animation?: 'spin' | 'pulse' | 'bounce' | 'shake' | 'beat' | 'fade' | 'beatFade' | 'spinPulse'
  preset?: 'loading'
  disabled?: boolean
}

const attrs = useAttrs()
const slots = useSlots()
const props = withDefaults(
  defineProps<ButtonBaseProps>(),
  {
    rounded: true,
    shadow: true,
    iconPosition: 'before',
  }
)

const disabledComputed = computed(() => props.preset === 'loading' ? true : props.disabled)
const isShowingIcon = computed<boolean>(() => iconProps.value !== undefined)
const isIconOnly = computed<boolean>(() => isShowingIcon.value && isSlotEmpty(slots.default))

const iconProps = computed(() => {
  if (props.preset === 'loading') {
    return { spin: true, icon: faSpinnerThird }
  }

  const icon = props.icon
  const animation = props.animation

  if (icon === undefined) {
    return undefined
  }

  if (animation !== undefined) {
    return { [animation]: true, icon }
  }

  return { icon }
})
</script>

<template>
  <button
    type="button"
    v-bind="attrs"
    :disabled="disabledComputed"
    class="btn"
    :class="{
      'rounded-pill': rounded,
      [$style.shadowed]: shadow,
      'btn-sm': size === 'small',
      'btn-lg': size === 'large'
    }">
    <FontAwesomeIcon
      v-if="isShowingIcon && iconPosition === 'before'"
      v-bind="iconProps!"
      :class="{ 'me-1': !isIconOnly }" />
    <slot />
    <FontAwesomeIcon
      v-if="isShowingIcon && iconPosition === 'after'"
      v-bind="iconProps!"
      :class="{ 'ms-1': !isIconOnly }" />
  </button>
</template>

<style lang="scss" module>
.shadowed {
  box-shadow: 2px 2px 4px rgba(0, 0, 0, 0.29);
}
</style>